import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, CheckCircle2, AlertCircle, Clock } from "lucide-react";

export function AdminSyncPanel({
  syncHealth,
  yieldCurveStatus,
  latestReportDate,
  formatDate,
  syncStatus,
  syncing,
  getSyncMessage,
  handleSync,
  syncJobs,
  liqHourlyEnabled,
  setLiqHourly,
}) {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          Email Sync Control
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <div
            data-testid="admin-sync-health-badge"
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
              syncHealth.status === "Healthy"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-amber-200 bg-amber-50 text-amber-700"
            }`}
          >
            <span data-testid="admin-sync-health-label">Sync Health: {syncHealth.status}</span>
            <span className="text-[11px] text-slate-600" data-testid="admin-sync-health-details">
              Cooldown {syncHealth.cooldownCount} · Rate-limit {syncHealth.rateLimitFails}
            </span>
          </div>
          <div
            data-testid="admin-yield-curve-badge"
            className={`inline-flex flex-wrap items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${yieldCurveStatus.tone}`}
          >
            <span data-testid="admin-yield-curve-label">Yield Curve: {yieldCurveStatus.label}</span>
            <span className="text-[11px] text-slate-600" data-testid="admin-yield-curve-value">{yieldCurveStatus.value}</span>
            <span className="text-[11px] text-slate-500" data-testid="admin-yield-curve-date">{yieldCurveStatus.date}</span>
            <span className="text-[11px] text-slate-600" data-testid="admin-yield-curve-streak">
              {yieldCurveStatus.streakLabel}: {yieldCurveStatus.streakValue}
            </span>
            <span className="text-[11px] text-slate-500" data-testid="admin-yield-curve-last-inversion">
              {yieldCurveStatus.inversionDateLabel}: {yieldCurveStatus.inversionDateValue}
            </span>
          </div>
          <div
            data-testid="admin-latest-report-badge"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700"
          >
            <span data-testid="admin-latest-report-label">Latest report:</span>
            <span data-testid="admin-latest-report-date" className="text-[11px] text-slate-600">
              {latestReportDate ? formatDate(latestReportDate) : "N/A"}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 text-sm ${(syncStatus?.status === "syncing" || syncStatus?.status === "queued") ? "text-blue-600" : "text-slate-600"}`}>
              {syncStatus?.status === "syncing" ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>{getSyncMessage("Syncing...")}</span>
                </>
              ) : syncStatus?.status === "queued" ? (
                <>
                  <Clock className="w-4 h-4" />
                  <span>{getSyncMessage("Sync queued")}</span>
                </>
              ) : syncStatus?.status === "error" ? (
                <>
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-500">Error: {syncStatus?.message}</span>
                </>
              ) : syncStatus?.last_sync ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <div className="flex flex-col leading-tight">
                    <span>Last sync: {formatDate(syncStatus.last_sync)}</span>
                    {(syncStatus?.symbols_failed || 0) > 0 && (
                      <span className="text-xs text-amber-600">{syncStatus.symbols_failed} symbol(s) had AI issues</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4" />
                  <span>Not synced yet</span>
                </>
              )}
            </div>
          </div>

          <Button
            data-testid="admin-refresh-btn"
            onClick={handleSync}
            disabled={syncing}
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Refreshing..." : "Manual Refresh"}
          </Button>
        </div>

        <p className="text-xs text-slate-500">Auto-sync runs daily at 08:00 CET. Use manual refresh only when needed.</p>

        <div data-testid="sync-jobs-panel" className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-slate-900">Sync Queue</div>
            <div data-testid="sync-queue-count" className="text-xs text-slate-500">Queued: {syncStatus?.queue_depth ?? 0}</div>
          </div>
          <div className="mt-3 space-y-2">
            {syncJobs.length ? syncJobs.map((job) => (
              <div
                key={job.id}
                data-testid={`sync-job-${job.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-700">{job.trigger || "manual"}</span>
                  <span className={`px-2 py-0.5 rounded-full ${
                    job.status === "completed"
                      ? "bg-emerald-100 text-emerald-700"
                      : job.status === "failed"
                        ? "bg-red-100 text-red-700"
                        : job.status === "running"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-slate-200 text-slate-700"
                  }`}>{job.status}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span>Requested: {formatDate(job.requested_at)}</span>
                  {job.completed_at && <span>Done: {formatDate(job.completed_at)}</span>}
                  {job.error && job.status === "failed" ? (
                    <span className="text-red-600" data-testid={`sync-job-error-${job.id}`}>{job.error}</span>
                  ) : null}
                </div>
              </div>
            )) : <div className="text-xs text-slate-400">No recent sync jobs.</div>}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div>
            <div className="text-sm font-medium text-slate-900">Hourly liquidation snapshots</div>
            <div className="text-xs text-slate-600">Collects Binance+Bybit liquidation events every hour (24/7) to improve persistence.</div>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="liquidation-hourly-status" className="text-xs text-slate-600">{liqHourlyEnabled ? "Enabled" : "Disabled"}</span>
            <Switch data-testid="liquidation-hourly-toggle" checked={liqHourlyEnabled} onCheckedChange={(value) => setLiqHourly(!!value)} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminBackfillPanel({ backfillStatus, backfillRunning, handleBackfill, formatDate }) {
  return (
    <Card data-testid="admin-backfill-card">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-lg">Price Backfill Status</CardTitle>
        <Button data-testid="admin-backfill-run-button" variant="outline" onClick={handleBackfill} disabled={backfillRunning} className="text-slate-700">
          <RefreshCw className={`w-4 h-4 mr-2 ${backfillRunning ? "animate-spin" : ""}`} />
          {backfillRunning ? "Backfilling..." : "Run 5-year backfill"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {backfillStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-700">
            {[
              ["admin-backfill-last-run", "Last refresh", backfillStatus.last_run ? formatDate(backfillStatus.last_run) : "N/A"],
              ["admin-backfill-coverage", "Coverage", backfillStatus.min_date && backfillStatus.max_date ? `${backfillStatus.min_date} → ${backfillStatus.max_date}` : "N/A"],
              ["admin-backfill-symbols", "Symbols", backfillStatus.symbols_count ?? "N/A"],
              ["admin-backfill-points", "Data points", backfillStatus.total_points ?? "N/A"],
              ["admin-backfill-lookback", "Lookback window", backfillStatus.lookback_days ? `${backfillStatus.lookback_days} days` : "N/A"],
              ["admin-backfill-run-type", "Run type", backfillStatus.run_type || "N/A"],
            ].map(([testId, label, value]) => (
              <div key={testId} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={testId}>
                <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
                <div className="mt-1 font-medium text-slate-900">{value}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-sm text-slate-500" data-testid="admin-backfill-empty">No backfill run has been recorded yet.</div>}
      </CardContent>
    </Card>
  );
}

export function AdminUsersSection({
  search,
  setSearch,
  fetchUsers,
  loading,
  createEmail,
  setCreateEmail,
  createLevel,
  setCreateLevel,
  createUser,
  users,
  updateUser,
  resendInvite,
  sendPasswordReset,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Users</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input data-testid="admin-user-search-input" placeholder="Search by email" value={search} onChange={(event) => setSearch(event.target.value)} />
          <Button data-testid="admin-user-search-button" variant="outline" onClick={fetchUsers} disabled={loading}>Search</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input data-testid="admin-create-user-email-input" placeholder="newuser@example.com" value={createEmail} onChange={(event) => setCreateEmail(event.target.value)} />
          <Select value={createLevel} onValueChange={setCreateLevel}>
            <SelectTrigger data-testid="admin-create-user-access-select"><SelectValue placeholder="Access level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="free">free</SelectItem>
              <SelectItem value="stripe">stripe</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="admin-create-user-submit-button" onClick={createUser} className="bg-slate-900 hover:bg-slate-800">Create user + send invite</Button>
        </div>

        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>AccessLevel</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead>Disabled</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <Select value={user.accessLevel || "stripe"} onValueChange={(value) => updateUser(user.id, { accessLevel: value })}>
                      <SelectTrigger data-testid={`user-access-level-${user.id}`} className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">free</SelectItem>
                        <SelectItem value="stripe">stripe</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{user.billingStatus || "none"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch data-testid={`user-disabled-toggle-${user.id}`} checked={!!user.disabled} onCheckedChange={(value) => updateUser(user.id, { disabled: value })} />
                      <span className="text-xs text-slate-600">{user.disabled ? "Yes" : "No"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button data-testid={`user-resend-invite-${user.id}`} variant="outline" size="sm" onClick={() => resendInvite(user.id)}>Resend invite</Button>
                      <Button data-testid={`user-send-reset-${user.id}`} variant="outline" size="sm" onClick={() => sendPasswordReset(user.id)}>Send reset</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}